import type { BlogPost } from '../../types/blog';

export const post: BlogPost = {
  category: 'estrategia',
  author: 'Ivan Henrique',
  publishedAt: '2026-03-30',
  coverImage: '/blog/mandante-visitante.svg',
  tags: ['Mandante', 'Visitante', 'Brasileirão', 'Estatística'],
  i18n: {
    'pt-BR': {
      slug: 'mandante-visitante',
      title: 'Como mandante e visitante mudam tudo: dados históricos do Brasileirão',
      excerpt:
        'A vantagem de jogar em casa no futebol brasileiro é uma das maiores do mundo. Veja os dados históricos do Brasileirão e como usar isso na hora de palpitar.',
      readTimeMinutes: 7,
      coverImageAlt: 'Capa: Mandante x Visitante no Brasileirão',
      keywords: ['mandante brasileirão', 'visitante futebol', 'jogo em casa', 'vantagem casa'],
      content: (
        <>
          <p>
            No futebol brasileiro, jogar em casa importa. Não é só ditado de
            torcedor — os dados confirmam. Ao longo das últimas décadas, a
            vantagem do mandante no Brasileirão é uma das maiores entre os
            grandes campeonatos do mundo. Pra quem joga bolão, ignorar esse
            fator é praticamente apostar contra a estatística.
          </p>

          <h2>Os números do Brasileirão</h2>
          <p>
            Considerando as últimas 5 a 10 temporadas do Brasileirão Série A,
            a média de resultados em jogos é, de forma aproximada:
          </p>
          <ul>
            <li><strong>Vitória do mandante:</strong> ~50%</li>
            <li><strong>Empate:</strong> ~25%</li>
            <li><strong>Vitória do visitante:</strong> ~25%</li>
          </ul>
          <p>
            Quer dizer: o mandante ganha em metade dos jogos. <strong>É o
            dobro de chance de ganhar comparado ao visitante</strong>. Em
            nenhuma outra grande liga essa diferença é tão acentuada.
          </p>

          <h2>Por que a vantagem é tão grande no Brasil</h2>
          <p>
            Vários fatores explicam:
          </p>

          <h3>1. Distâncias gigantes</h3>
          <p>
            O Brasil é um país de proporções continentais. <strong>Times do
            Sul jogando no Nordeste enfrentam viagem de 5 a 7 horas de
            voo</strong>, mudança de fuso horário (1 hora), calor extremo se
            for verão. O cansaço da viagem se reflete no campo.
          </p>

          <h3>2. Diferenças climáticas</h3>
          <p>
            Jogar em Belém ou Manaus em julho (verão amazônico) é muito
            diferente de jogar em Porto Alegre em maio (outono frio). <strong>O
            time visitante quase nunca está habituado ao clima
            local</strong>. Calor extremo, umidade, friagem do Sul — tudo
            atrapalha quem não vive lá.
          </p>

          <h3>3. Torcida brasileira</h3>
          <p>
            Torcida brasileira é particularmente intensa. <strong>Estádios
            cheios em finais de semana criam pressão psicológica gigante
            sobre o visitante</strong>. Jogadores reclamam recorrentemente
            de hostilidade, gestos, vaias durante todo o jogo. Pode parecer
            bobagem, mas afeta concentração.
          </p>

          <h3>4. Gramados de qualidade variável</h3>
          <p>
            Times mandantes conhecem o próprio gramado — sabem onde tem buraco,
            onde a bola pula mais. <strong>Times visitantes precisam se
            adaptar em 15 minutos de aquecimento</strong>. Isso faz diferença,
            especialmente em jogadas de bola parada e nos primeiros minutos
            de jogo.
          </p>

          <h2>Quais times exploram melhor o mando</h2>
          <p>
            Nem todo time tira o mesmo proveito de jogar em casa. Alguns são
            praticamente intransponíveis em seus estádios; outros não fazem
            diferença grande entre casa e fora.
          </p>
          <p>
            Sem citar clubes específicos (porque varia ano a ano), o padrão
            geral é:
          </p>
          <ul>
            <li>
              <strong>Times do Norte e Nordeste (em casa):</strong> percentual
              de vitórias em casa frequentemente acima de 60%.
            </li>
            <li>
              <strong>Times do Sul (em casa):</strong> também forte, mas
              ligeiramente menor por causa do clima mais ameno (atletas
              adaptáveis).
            </li>
            <li>
              <strong>Times do Rio e São Paulo:</strong> mandos bons, mas o
              calendário cheio e fadiga afetam ocasionalmente o desempenho
              em casa também.
            </li>
          </ul>

          <h2>Como aplicar no bolão</h2>

          <h3>1. Dê mais peso ao mandante</h3>
          <p>
            Na dúvida entre apostar no time A ou empate, e o mandante for o
            time A, vá com o mandante. <strong>Estatisticamente, é a aposta
            mais segura</strong> em jogos equilibrados.
          </p>

          <h3>2. Conheça os mandos extremos</h3>
          <p>
            Há times que praticamente não perdem em casa. Outros que perdem
            com regularidade mesmo jogando em casa. <strong>Antes da
            temporada começar, anote os 3-4 times com mando mais
            forte</strong> — isso vai pagar a temporada inteira.
          </p>

          <h3>3. Cuidado com visitantes do Sul indo pro Norte</h3>
          <p>
            Em jogos entre time do Sul (RS, SC, PR) visitando o Norte ou
            Nordeste em meses quentes (novembro a março), <strong>a chance de
            vitória do visitante cai pra cerca de 18%</strong>. É um dos
            cenários mais previsíveis do calendário.
          </p>

          <h3>4. Ajuste placares pra menos goleadas fora de casa</h3>
          <p>
            Em jogos fora de casa, mesmo quando o visitante é favorito, é
            raro vencer por mais de 2 gols de diferença. <strong>Goleadas
            do visitante são exceção</strong>. Em vez de 4x0 ou 3x0 fora de
            casa, palpite 2x0 ou 2x1.
          </p>

          <h2>Quando o mandante decepciona</h2>
          <p>
            Há situações específicas onde a vantagem do mandante cai:
          </p>
          <ul>
            <li>
              <strong>Time mandante em má fase</strong> (4 ou 5 jogos sem vencer):
              a confiança cai, a torcida vaia, a vantagem psicológica vira
              desvantagem.
            </li>
            <li>
              <strong>Jogo entre rebaixáveis</strong>: tensão excessiva faz
              ambos os times errarem demais.
            </li>
            <li>
              <strong>Clima muito extremo</strong>: chuva forte, temperatura
              acima de 35°C — diminuem a qualidade do jogo do mandante
              também.
            </li>
          </ul>

          <h2>Conclusão</h2>
          <p>
            Mandante e visitante são variáveis sub-utilizadas pelo
            palpiteiro médio. Quem incorpora esse fator nas decisões
            consegue uma vantagem significativa ao longo de uma temporada
            do Brasileirão. Antes de palpitar, pergunte-se: <em>quem joga em
            casa? E é casa pra valer (com torcida, com gramado conhecido) ou
            casa nominal?</em>
          </p>
          <p>
            Confira mais artigos no <a href="/blog">blog</a> ou veja o{' '}
            <a href="/glossario">glossário</a> pra entender termos técnicos.
          </p>
        </>
      ),
    },
    en: {
      slug: 'home-vs-away',
      title: 'How home and away change everything: historical data from the Brazilian league',
      excerpt:
        'The home advantage in Brazilian football is one of the biggest in the world. See the historical data from the Brasileirão and how to use it when making predictions.',
      readTimeMinutes: 7,
      coverImageAlt: 'Cover: Home vs Away in the Brazilian league',
      coverImage: '/blog/mandante-visitante-en.svg',
      keywords: ['home team brazilian league', 'away football', 'playing at home', 'home advantage'],
      content: (
        <>
          <p>
            In Brazilian football, playing at home matters. It's not just a
            fan's saying — the data confirms it. Over the past few decades,
            the home advantage in the Brazilian league (Brasileirão) is one of
            the biggest among the major championships in the world. For pool
            players, ignoring this factor is practically betting against the
            statistics.
          </p>

          <h2>The Brazilian league numbers</h2>
          <p>
            Considering the last 5 to 10 seasons of the Brasileirão Série A,
            the average results in matches are, roughly:
          </p>
          <ul>
            <li><strong>Home win:</strong> ~50%</li>
            <li><strong>Draw:</strong> ~25%</li>
            <li><strong>Away win:</strong> ~25%</li>
          </ul>
          <p>
            In other words: the home team wins half the matches. <strong>That
            is double the chance of winning compared to the away team</strong>.
            In no other major league is this difference so pronounced.
          </p>

          <h2>Why the advantage is so big in Brazil</h2>
          <p>
            Several factors explain it:
          </p>

          <h3>1. Huge distances</h3>
          <p>
            Brazil is a country of continental proportions. <strong>Teams from
            the South playing in the Northeast face a flight of 5 to 7
            hours</strong>, a time zone change (1 hour), extreme heat if it's
            summer. The fatigue of the trip shows on the pitch.
          </p>

          <h3>2. Climate differences</h3>
          <p>
            Playing in Belém or Manaus in July (Amazonian summer) is very
            different from playing in Porto Alegre in May (chilly autumn).{' '}
            <strong>The away team is almost never used to the local
            climate</strong>. Extreme heat, humidity, the cold of the South —
            it all hampers anyone who doesn't live there.
          </p>

          <h3>3. The Brazilian crowd</h3>
          <p>
            The Brazilian crowd is particularly intense. <strong>Full stadiums
            on weekends create huge psychological pressure on the away
            team</strong>. Players regularly complain about hostility,
            gestures, jeering throughout the whole match. It may sound
            trivial, but it affects concentration.
          </p>

          <h3>4. Pitches of varying quality</h3>
          <p>
            Home teams know their own pitch — they know where there's a hole,
            where the ball bounces more. <strong>Away teams have to adapt in
            15 minutes of warm-up</strong>. That makes a difference,
            especially on set pieces and in the opening minutes of the match.
          </p>

          <h2>Which teams make the best use of home advantage</h2>
          <p>
            Not every team gets the same benefit from playing at home. Some
            are practically impassable in their stadiums; others don't make a
            big difference between home and away.
          </p>
          <p>
            Without naming specific clubs (because it varies year to year),
            the general pattern is:
          </p>
          <ul>
            <li>
              <strong>Teams from the North and Northeast (at home):</strong>{' '}
              their home-win percentage is frequently above 60%.
            </li>
            <li>
              <strong>Teams from the South (at home):</strong> also strong,
              but slightly lower because of the milder climate (athletes who
              adapt more easily).
            </li>
            <li>
              <strong>Teams from Rio and São Paulo:</strong> good home
              records, but the packed calendar and fatigue occasionally affect
              their home performance too.
            </li>
          </ul>

          <h2>How to apply this in your pool</h2>

          <h3>1. Give more weight to the home team</h3>
          <p>
            When in doubt between betting on team A or a draw, and the home
            team is team A, go with the home team. <strong>Statistically, it's
            the safer bet</strong> in evenly matched games.
          </p>

          <h3>2. Know the extreme home records</h3>
          <p>
            There are teams that practically never lose at home. Others that
            lose regularly even when playing at home. <strong>Before the
            season starts, note down the 3-4 teams with the strongest home
            record</strong> — that will pay off all season long.
          </p>

          <h3>3. Watch out for Southern away teams travelling to the North</h3>
          <p>
            In matches with a Southern team (RS, SC, PR) visiting the North or
            Northeast in hot months (November to March), <strong>the chance of
            an away win drops to around 18%</strong>. It's one of the most
            predictable scenarios on the calendar.
          </p>

          <h3>4. Adjust scores for fewer away thrashings</h3>
          <p>
            In away matches, even when the away team is the favourite, it's
            rare to win by more than a 2-goal margin. <strong>Away thrashings
            are the exception</strong>. Instead of 4-0 or 3-0 away from home,
            predict 2-0 or 2-1.
          </p>

          <h2>When the home team disappoints</h2>
          <p>
            There are specific situations where the home advantage drops:
          </p>
          <ul>
            <li>
              <strong>Home team in poor form</strong> (4 or 5 matches without
              a win): confidence drops, the crowd jeers, the psychological
              advantage turns into a disadvantage.
            </li>
            <li>
              <strong>Match between relegation-zone teams</strong>: excessive
              tension makes both teams make too many mistakes.
            </li>
            <li>
              <strong>Very extreme weather</strong>: heavy rain, temperature
              above 35°C — these lower the quality of the home team's game
              too.
            </li>
          </ul>

          <h2>Conclusion</h2>
          <p>
            Home and away are variables that are underused by the average
            predictor. Whoever builds this factor into their decisions gains a
            significant edge over a Brazilian league season. Before predicting,
            ask yourself: <em>who's playing at home? And is it really home
            (with the crowd, with a familiar pitch) or home in name only?</em>
          </p>
          <p>
            Check out more articles on the <a href="/en/blog">blog</a> or see
            the <a href="/en/glossary">glossary</a> to understand technical
            terms.
          </p>
        </>
      ),
    },
    es: {
      slug: 'local-vs-visitante',
      title: 'Cómo el local y el visitante lo cambian todo: datos históricos del Brasileirão',
      excerpt:
        'La ventaja de jugar de local en el fútbol brasileño es una de las mayores del mundo. Mira los datos históricos del Brasileirão y cómo usar eso a la hora de pronosticar.',
      readTimeMinutes: 7,
      coverImageAlt: 'Portada: Local vs Visitante en el Brasileirão',
      coverImage: '/blog/mandante-visitante-es.svg',
      keywords: ['local brasileirão', 'visitante fútbol', 'jugar de local', 'ventaja de local'],
      content: (
        <>
          <p>
            En el fútbol brasileño, jugar de local importa. No es solo un dicho
            de hincha — los datos lo confirman. A lo largo de las últimas
            décadas, la ventaja del local en el Brasileirão es una de las
            mayores entre los grandes campeonatos del mundo. Para quien juega la
            quiniela, ignorar este factor es prácticamente apostar contra la
            estadística.
          </p>

          <h2>Los números del Brasileirão</h2>
          <p>
            Considerando las últimas 5 a 10 temporadas del Brasileirão Série A,
            la media de resultados en los partidos es, de forma aproximada:
          </p>
          <ul>
            <li><strong>Victoria del local:</strong> ~50%</li>
            <li><strong>Empate:</strong> ~25%</li>
            <li><strong>Victoria del visitante:</strong> ~25%</li>
          </ul>
          <p>
            Es decir: el local gana en la mitad de los partidos. <strong>Es el
            doble de posibilidad de ganar comparado con el visitante</strong>.
            En ninguna otra gran liga esa diferencia es tan acentuada.
          </p>

          <h2>Por qué la ventaja es tan grande en Brasil</h2>
          <p>
            Varios factores lo explican:
          </p>

          <h3>1. Distancias enormes</h3>
          <p>
            Brasil es un país de proporciones continentales. <strong>Los
            equipos del Sur que juegan en el Nordeste enfrentan un viaje de 5 a
            7 horas de vuelo</strong>, cambio de huso horario (1 hora), calor
            extremo si es verano. El cansancio del viaje se refleja en la
            cancha.
          </p>

          <h3>2. Diferencias climáticas</h3>
          <p>
            Jugar en Belém o Manaos en julio (verano amazónico) es muy distinto
            de jugar en Porto Alegre en mayo (otoño frío). <strong>El equipo
            visitante casi nunca está habituado al clima local</strong>. Calor
            extremo, humedad, el frío del Sur — todo le complica a quien no vive
            allí.
          </p>

          <h3>3. La afición brasileña</h3>
          <p>
            La afición brasileña es particularmente intensa. <strong>Los
            estadios llenos los fines de semana crean una presión psicológica
            enorme sobre el visitante</strong>. Los jugadores se quejan de forma
            recurrente de hostilidad, gestos, silbidos durante todo el partido.
            Puede parecer una tontería, pero afecta la concentración.
          </p>

          <h3>4. Canchas de calidad variable</h3>
          <p>
            Los equipos locales conocen su propia cancha — saben dónde hay un
            bache, dónde la pelota pica más. <strong>Los equipos visitantes
            tienen que adaptarse en 15 minutos de calentamiento</strong>. Eso
            marca la diferencia, especialmente en jugadas a balón parado y en
            los primeros minutos del partido.
          </p>

          <h2>Qué equipos aprovechan mejor el factor local</h2>
          <p>
            No todos los equipos sacan el mismo provecho de jugar de local.
            Algunos son prácticamente infranqueables en sus estadios; otros no
            marcan gran diferencia entre local y visitante.
          </p>
          <p>
            Sin citar clubes específicos (porque varía año a año), el patrón
            general es:
          </p>
          <ul>
            <li>
              <strong>Equipos del Norte y Nordeste (de local):</strong> el
              porcentaje de victorias en casa suele estar por encima del 60%.
            </li>
            <li>
              <strong>Equipos del Sur (de local):</strong> también fuerte, pero
              ligeramente menor por el clima más templado (atletas que se
              adaptan más fácil).
            </li>
            <li>
              <strong>Equipos de Río y São Paulo:</strong> buenos registros de
              local, pero el calendario cargado y la fatiga afectan
              ocasionalmente el rendimiento en casa también.
            </li>
          </ul>

          <h2>Cómo aplicarlo en la quiniela</h2>

          <h3>1. Dale más peso al local</h3>
          <p>
            En la duda entre apostar al equipo A o al empate, y si el local es
            el equipo A, ve con el local. <strong>Estadísticamente, es la
            apuesta más segura</strong> en partidos parejos.
          </p>

          <h3>2. Conoce los registros de local extremos</h3>
          <p>
            Hay equipos que prácticamente no pierden de local. Otros que pierden
            con regularidad incluso jugando en casa. <strong>Antes de que
            empiece la temporada, anota los 3-4 equipos con el factor local más
            fuerte</strong> — eso te va a rendir toda la temporada.
          </p>

          <h3>3. Cuidado con visitantes del Sur yendo al Norte</h3>
          <p>
            En partidos de un equipo del Sur (RS, SC, PR) visitando el Norte o
            el Nordeste en meses calurosos (noviembre a marzo), <strong>la
            posibilidad de victoria del visitante cae a cerca del 18%</strong>.
            Es uno de los escenarios más previsibles del calendario.
          </p>

          <h3>4. Ajusta los marcadores para menos goleadas fuera de casa</h3>
          <p>
            En partidos fuera de casa, incluso cuando el visitante es favorito,
            es raro ganar por más de 2 goles de diferencia. <strong>Las
            goleadas del visitante son la excepción</strong>. En lugar de 4-0 o
            3-0 fuera de casa, pronostica 2-0 o 2-1.
          </p>

          <h2>Cuándo el local decepciona</h2>
          <p>
            Hay situaciones específicas en las que la ventaja del local cae:
          </p>
          <ul>
            <li>
              <strong>Equipo local en mala racha</strong> (4 o 5 partidos sin
              ganar): la confianza cae, la afición silba, la ventaja
              psicológica se vuelve desventaja.
            </li>
            <li>
              <strong>Partido entre equipos en zona de descenso</strong>: la
              tensión excesiva hace que ambos equipos fallen demasiado.
            </li>
            <li>
              <strong>Clima muy extremo</strong>: lluvia fuerte, temperatura por
              encima de 35°C — reducen la calidad del juego del local también.
            </li>
          </ul>

          <h2>Conclusión</h2>
          <p>
            El local y el visitante son variables infrautilizadas por el
            pronosticador medio. Quien incorpora este factor en sus decisiones
            consigue una ventaja significativa a lo largo de una temporada del
            Brasileirão. Antes de pronosticar, pregúntate: <em>¿quién juega de
            local? ¿Y es local de verdad (con afición, con cancha conocida) o
            local solo de nombre?</em>
          </p>
          <p>
            Mira más artículos en el <a href="/es/blog">blog</a> o consulta el{' '}
            <a href="/es/glosario">glosario</a> para entender términos
            técnicos.
          </p>
        </>
      ),
    },
  },
};
